import { useState, useEffect } from 'react';
import { useSiteContext } from '../contexts/SiteContext';
import {
  AlertCircle, Loader, Search, MousePointer, Eye, TrendingUp,
  Globe, Smartphone, Monitor, Tablet, RefreshCw, ChevronDown,
  CheckCircle, ExternalLink, ArrowUp, ArrowDown
} from 'lucide-react';

const SearchConsole = ({ user }) => {
  const { selectedSite } = useSiteContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [sites, setSites] = useState([]);
  const [showSiteSelect, setShowSiteSelect] = useState(false);
  const [dateRange, setDateRange] = useState('28d');

  useEffect(() => {
    if (selectedSite) {
      fetchData();
    } else {
      setLoading(false);
      setError('Please select a site');
    }
  }, [selectedSite]);

  const fetchData = async () => {
    if (!selectedSite) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/app/api/google/search-console.php?siteId=${selectedSite.id}&dateRange=${dateRange}`,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        if (result.data) {
          setData(result.data);
          if (!result.data.connected) {
            // Not connected to Google at all
            setError('Google not connected. Please connect via Google Analytics first.');
          } else if (!result.data.site_selected) {
            // Connected but no GSC site selected
            fetchSites();
          }
        }
      } else {
        setError(result.message || 'Failed to fetch Search Console data');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSites = async () => {
    if (!selectedSite) return;

    try {
      const response = await fetch(
        `/app/api/google/search-console-sites.php?siteId=${selectedSite.id}`,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setSites(result.data.sites || []);
        setShowSiteSelect(true);
      } else {
        setError(result.message || 'Failed to fetch Search Console sites');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    }
  };

  const selectSite = async (siteUrl) => {
    if (!selectedSite) return;

    try {
      const response = await fetch('/app/api/google/search-console-select.php', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ siteId: selectedSite.id, gscSiteUrl: siteUrl })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setShowSiteSelect(false);
        fetchData();
      } else {
        setError(result.message || 'Failed to select site');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    }
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
  };

  useEffect(() => {
    if (data?.connected && data?.site_selected) {
      fetchData();
    }
  }, [dateRange]);

  const getDeviceIcon = (device) => {
    switch (device?.toLowerCase()) {
      case 'mobile': return <Smartphone className="h-5 w-5" />;
      case 'tablet': return <Tablet className="h-5 w-5" />;
      default: return <Monitor className="h-5 w-5" />;
    }
  };

  const getPositionColor = (position) => {
    if (position <= 3) return 'text-green-600';
    if (position <= 10) return 'text-blue-600';
    if (position <= 20) return 'text-yellow-600';
    return 'text-gray-600';
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading Search Console...</p>
        </div>
      </div>
    );
  }

  if (!selectedSite) {
    return (
      <div className="p-6">
        <div className="rounded-lg bg-yellow-50 p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-yellow-600" />
          <p className="text-yellow-800">Please select a site</p>
        </div>
      </div>
    );
  }

  // Not connected state
  if (!data?.connected) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Search className="h-8 w-8 text-green-600" />
              Google Search Console
            </h1>
            <p className="text-gray-600 mt-2">View your search performance data from Google</p>
          </div>

          {error && (
            <div className="mb-6 rounded-lg bg-red-50 p-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <p className="text-red-800">{error}</p>
            </div>
          )}

          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Search className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Connect Google First</h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              To use Search Console, please connect your Google account via Google Analytics first.
              The same connection provides access to both services.
            </p>
            <a
              href="#google-analytics"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Go to Google Analytics
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Site selection
  if (showSiteSelect || (data?.connected && !data?.site_selected)) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Search className="h-8 w-8 text-green-600" />
              Select Search Console Property
            </h1>
            <p className="text-gray-600 mt-2">Choose which website to view Search Console data for</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-green-700 font-medium">Connected to Google</span>
            </div>

            {sites.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm text-gray-600 mb-4">Select a verified property:</p>
                {sites.map((site, idx) => (
                  <button
                    key={idx}
                    onClick={() => selectSite(site.site_url)}
                    className="w-full text-left p-4 border rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors"
                  >
                    <div className="font-medium text-gray-900">{site.site_url}</div>
                    <div className="text-sm text-gray-500 capitalize">{site.permission_level}</div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No verified sites found in Search Console.</p>
                <p className="text-sm mt-2">
                  <a
                    href="https://search.google.com/search-console"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Add your site in Google Search Console
                  </a>
                </p>
              </div>
            )}

            <div className="mt-6 pt-4 border-t flex justify-end">
              <button
                onClick={fetchSites}
                className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh List
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main data view
  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Search className="h-8 w-8 text-green-600" />
              Google Search Console
            </h1>
            <p className="text-gray-600 mt-1">
              {data?.site_url}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Date Range */}
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              {['7d', '28d', '3m', '6m'].map((range) => (
                <button
                  key={range}
                  onClick={() => handleDateRangeChange(range)}
                  className={`px-3 py-2 text-sm ${
                    dateRange === range
                      ? 'bg-green-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {range}
                </button>
              ))}
            </div>
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => setShowSiteSelect(true)}
              className="p-2 text-gray-500 hover:text-gray-700"
              title="Change Property"
            >
              <ChevronDown className="h-5 w-5" />
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-lg bg-red-50 p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {data && (
          <div className="space-y-6">
            {/* Overview Cards */}
            {data.overview && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MousePointer className="h-5 w-5 text-blue-600" />
                    <span className="text-sm text-gray-500">Total Clicks</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {data.overview.clicks?.toLocaleString()}
                  </p>
                </div>

                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-gray-500">Impressions</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {data.overview.impressions?.toLocaleString()}
                  </p>
                </div>

                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-5 w-5 text-purple-600" />
                    <span className="text-sm text-gray-500">Avg CTR</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {data.overview.ctr}%
                  </p>
                </div>

                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <ArrowUp className="h-5 w-5 text-orange-600" />
                    <span className="text-sm text-gray-500">Avg Position</span>
                  </div>
                  <p className={`text-2xl font-bold ${getPositionColor(data.overview.position)}`}>
                    {data.overview.position}
                  </p>
                </div>
              </div>
            )}

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Queries */}
              {data.queries && data.queries.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Search Queries</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b">
                          <th className="pb-2">Query</th>
                          <th className="pb-2 text-right">Clicks</th>
                          <th className="pb-2 text-right">Impr.</th>
                          <th className="pb-2 text-right">CTR</th>
                          <th className="pb-2 text-right">Pos.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.queries.map((q, idx) => (
                          <tr key={idx} className="border-b border-gray-100 last:border-0">
                            <td className="py-2 max-w-[200px] truncate" title={q.query}>
                              {q.query}
                            </td>
                            <td className="py-2 text-right font-medium">{q.clicks.toLocaleString()}</td>
                            <td className="py-2 text-right text-gray-600">{q.impressions.toLocaleString()}</td>
                            <td className="py-2 text-right text-gray-600">{q.ctr}%</td>
                            <td className={`py-2 text-right ${getPositionColor(q.position)}`}>
                              {q.position}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Top Pages */}
              {data.pages && data.pages.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Pages</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b">
                          <th className="pb-2">Page</th>
                          <th className="pb-2 text-right">Clicks</th>
                          <th className="pb-2 text-right">Impr.</th>
                          <th className="pb-2 text-right">CTR</th>
                          <th className="pb-2 text-right">Pos.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.pages.map((p, idx) => (
                          <tr key={idx} className="border-b border-gray-100 last:border-0">
                            <td className="py-2 max-w-[200px] truncate" title={p.full_url}>
                              {p.page}
                            </td>
                            <td className="py-2 text-right font-medium">{p.clicks.toLocaleString()}</td>
                            <td className="py-2 text-right text-gray-600">{p.impressions.toLocaleString()}</td>
                            <td className="py-2 text-right text-gray-600">{p.ctr}%</td>
                            <td className={`py-2 text-right ${getPositionColor(p.position)}`}>
                              {p.position}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Bottom Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Countries */}
              {data.countries && data.countries.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Globe className="h-5 w-5 text-green-600" />
                    Top Countries
                  </h3>
                  <div className="space-y-2">
                    {data.countries.map((country, idx) => {
                      const maxClicks = Math.max(...data.countries.map(c => c.clicks));
                      const percentage = maxClicks > 0 ? (country.clicks / maxClicks) * 100 : 0;
                      return (
                        <div key={idx}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-900">{country.country}</span>
                            <span className="text-sm text-gray-600">{country.clicks.toLocaleString()} clicks</span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-green-500 rounded-full"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Devices */}
              {data.devices && data.devices.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Devices</h3>
                  <div className="grid grid-cols-3 gap-4">
                    {data.devices.map((device, idx) => {
                      const total = data.devices.reduce((sum, d) => sum + d.clicks, 0);
                      const percentage = total > 0 ? Math.round((device.clicks / total) * 100) : 0;
                      return (
                        <div key={idx} className="text-center p-4 bg-gray-50 rounded-lg">
                          <div className="flex justify-center mb-2 text-gray-600">
                            {getDeviceIcon(device.device)}
                          </div>
                          <p className="text-2xl font-bold text-gray-900">{percentage}%</p>
                          <p className="text-sm text-gray-500">{device.device}</p>
                          <p className="text-xs text-gray-400">{device.clicks.toLocaleString()} clicks</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Daily Trend */}
            {data.daily && data.daily.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Over Time</h3>
                <div className="overflow-x-auto">
                  <div className="min-w-[600px]">
                    {/* Simple bar chart visualization */}
                    <div className="flex items-end gap-1 h-40">
                      {data.daily.map((day, idx) => {
                        const maxClicks = Math.max(...data.daily.map(d => d.clicks));
                        const height = maxClicks > 0 ? (day.clicks / maxClicks) * 100 : 0;
                        return (
                          <div
                            key={idx}
                            className="flex-1 bg-green-500 rounded-t hover:bg-green-600 transition-colors group relative"
                            style={{ height: `${height}%`, minHeight: '4px' }}
                            title={`${day.date}: ${day.clicks} clicks`}
                          >
                            <div className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 p-2 bg-gray-900 text-white text-xs rounded whitespace-nowrap">
                              {day.date}<br />
                              {day.clicks} clicks
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-between mt-2 text-xs text-gray-500">
                      <span>{data.daily[0]?.date}</span>
                      <span>{data.daily[data.daily.length - 1]?.date}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchConsole;
